import React, { useEffect, useMemo, useState } from 'react';
import { View, ScrollView } from 'react-native';
import { styles } from './editVehicle.styles';
import Typography from '../../../../components/Text/Text';
import InputField from '../../../../components/input/input';
import Button from '../../../../components/Button/Button';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getVehicleAddTour } from 'src/features/tour/tourSteps';
import RadioModal from '../../../../components/RadioModal/RadioModal';
import { useLinkTo, useRoute } from '@react-navigation/native';
import IconInput from '../../../../components/IconInput/IconInput';
import arrowDown from 'src/assets/arrowDown-3x.png';
import map from 'src/assets/map.png';
import MapModal from '../../../../components/MapModal/MapModal';
import { Address, initialMapAddress } from 'src/types/common/address';
import { formatAddressToString } from 'src/helpers/addressHelper';
import { Vehicle } from '@typeRoots/common/vehicles';
import { useCreateVehicleMutation, useUpdateVehicleMutation } from '../../api/vehicleApi'; // Ensure this API exists
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import {
  setCurrentVehicle,
  toggleVehicleToast,
  setLastRadiusTravelled,
} from 'src/features/vehicles/slice/vehicleSlice';
import { emptyVehicle } from './emptyVehiclesState';
import { URLS } from 'src/constants/urls';
import SwitchButtons from 'src/components/SwitchButtons/SwitchButtons';
import { handleOpenModal } from './editVehicle.functions';
import Toast from 'src/components/Toast/Toast';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { handleInputChange } from '../../helpers/vehicleHelpers';

const VehiclesEditScreen: React.FC = () => {
  const currentVehicle = useSelector(
    (state: RootState) => state.vehicles.currentVehicle,
  ) as Vehicle;
  const dispatch = useDispatch();

  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const lastRadiusTravelled = useSelector((state: RootState) => state.vehicles.lastRadiusTravelled);

  const [vehicleDetails, setVehicleDetails] = useState<Vehicle>(emptyVehicle);
  const [isTruck, setIsTruck] = useState(true);
  const [stateValue, setStateValue] = useState('0');
  const [equipmentValue, setEquipmentValue] = useState('0');
  const [isLoan, setIsLoan] = useState(false);
  const [vehicleSaveErrorToast, setVehicleSaveErrorToast] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [lossPayeeAddress, setLossPayeeAddress] = useState<Address>(initialMapAddress);
  const [valuationType, setValuationType] = useState(
    accountType === 'producer' ? 'Stated Value' : '',
  );
  const [isValuationTypeModalVisible, setIsValuationTypeModalVisible] = useState(false);

  const [modalConfig, setModalConfig] = useState({
    isVisible: false,
    type: '',
    title: '',
    options: [] as { label: string }[],
  });

  const openModal = (type: 'radiusTravelled' | 'classCode' | 'bodyType') =>
    handleOpenModal(type, isTruck, setModalConfig);

  const linkTo = useLinkTo();
  const route = useRoute();

  let isUpdateMode = false;
  let routeValue = null;

  if (route.params) {
    let { id } = route.params as { id?: number };
    if (id) {
      routeValue = route.params;
      isUpdateMode = !!routeValue;
    }
  }

  const [createVehicle] = useCreateVehicleMutation();
  const [updateVehicle] = useUpdateVehicleMutation();

  const isSaveEnabled = useMemo(() => {
    return (
      vehicleDetails.year?.length == 4 &&
      vehicleDetails.make?.length > 0 &&
      vehicleDetails.model?.length > 0 &&
      stateValue != null &&
      // stateValue !== '' &&
      equipmentValue != null &&
      // equipmentValue !== '' &&
      (!isLoan ||
        (isLoan &&
          vehicleDetails.lossPayeeName &&
          vehicleDetails.lossPayeeName?.length > 0 &&
          lossPayeeAddress.street.length > 0)) &&
      (!isTruck || (vehicleDetails.classCode && vehicleDetails.classCode?.length > 0)) &&
      vehicleDetails.bodyType &&
      vehicleDetails.bodyType?.length > 0 &&
      vehicleDetails.radiusTravelled &&
      vehicleDetails.radiusTravelled?.length > 0
    );
  }, [
    vehicleDetails.year,
    vehicleDetails.make,
    vehicleDetails.model,
    vehicleDetails.lossPayeeName,
    vehicleDetails.classCode,
    vehicleDetails.bodyType,
    vehicleDetails.radiusTravelled,
    stateValue,
    equipmentValue,
    isLoan,
    isTruck,
    lossPayeeAddress.street,
  ]);

  function remapClassCode(classCode: string | null): string | null {
    if (!classCode) return null; // If no class code is provided, return null

    // Define the mapping

    const mapping: { [key: string]: string } = {
      '2a': 'Class 2a: 6,001-8,500 pounds (2,722-3,856 kg)',
      '2b': 'Class 2b: 8,501-10,000 pounds (3,856-4,536 kg)',
      '3': 'Class 3: 10,001-14,000 pounds (4,536-6,350 kg)',
      '4': 'Class 4: 14,001-16,000 pounds (6,351-7,257 kg)',
      '5': 'Class 5: 16,001-19,500 pounds (7,258-8,845 kg)',
      '6': 'Class 6: 19,501-26,000 pounds (8,846-11,793 kg)',
      '7': 'Class 7: 26,001-33,000 pounds (11,794-14,969 kg)',
      '8': 'Class 8: 33,001 pounds (14,969 kg) +',
    };

    // Return the mapped value or a default message
    return mapping[classCode] || null;
  }

  useEffect(() => {
    if (currentVehicle) {
      let classCode = '';
      if (remapClassCode(currentVehicle.classCode)) {
        classCode = remapClassCode(currentVehicle.classCode) as string;
      } else {
        classCode = (currentVehicle.classCode as string) || '';
      }
      setVehicleDetails({
        ...vehicleDetails,
        ...currentVehicle,
        classCode: classCode || '',
        type: currentVehicle?.type || '',
        // Prefill the remembered radius for a new vehicle; never override an existing one
        radiusTravelled:
          currentVehicle?.radiusTravelled || (!isUpdateMode ? lastRadiusTravelled : '') || '',
      });
      setIsTruck(currentVehicle?.type === 'truck');
      setEquipmentValue(currentVehicle.equipmentValue?.amount?.toString() ?? '0');
      setStateValue(currentVehicle.value?.amount?.toString() ?? '0');
      setValuationType(
        currentVehicle.valuationType || (accountType === 'producer' ? 'Stated Value' : ''),
      );

      // Initialize loss payee address
      if (currentVehicle.lossPayeeAddress) {
        setLossPayeeAddress(currentVehicle.lossPayeeAddress);
      }
    }
  }, [currentVehicle]);

  useEffect(() => {
    if (currentVehicle.lossPayeeName && currentVehicle.lossPayeeName.length > 0) {
      setIsLoan(true);
    } else {
      setIsLoan(false);
    }
    dispatch(toggleVehicleToast(false));
  }, []);

  useEffect(() => {
    // gtm event on screen render
    trackGTMEvent(
      'vehicle-details-screen-landed',
      {
        userEvent: 'User landed on company details screen',
      },
      accountType,
    );
  }, []);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'vehicleAdd',
    driverSteps: getVehicleAddTour(),
    enabled: vehicleDetails !== undefined && currentVehicle !== undefined,
  });

  const handleChange = (field: keyof Vehicle, value: string) => {
    handleInputChange(field, value, (field, updatedValue) => {
      setVehicleDetails((prevDetails) => ({ ...prevDetails, [field]: updatedValue }));
    });
  };

  const handleSaveLossPayeeAddress = (newAddress: Address) => {
    setLossPayeeAddress(newAddress);
    setVehicleDetails((prevDetails) => ({
      ...prevDetails,
      lossPayeeAddress: newAddress,
    }));
    setIsMapVisible(false);
  };

  const extractClassCode = (input: string): string | null => {
    const regex = /Class\s(\d+[a-zA-Z]?)/; // Matches "Class " followed by digits and optional letters
    const match = input.match(regex); // Find the first match

    return match ? match[1] : null; // Return the captured class code or null if no match
  };

  const prepareVehicleData = (): Vehicle => ({
    ...vehicleDetails,
    value: {
      amount: stateValue || '0',
      currency: 'USD',
    },
    equipmentValue: {
      amount: equipmentValue || '0',
      currency: 'USD',
    },
    classCode: isTruck ? extractClassCode(vehicleDetails.classCode as string) : null,
    type: isTruck ? 'truck' : 'trailer',
    valuationType: valuationType || undefined,
    lossPayeeName: isLoan ? vehicleDetails.lossPayeeName : '',
    lossPayeeAddress: isLoan && lossPayeeAddress.street.length > 0 ? lossPayeeAddress : null,
    company: { id: currentCompany?.id || 0 },
  });

  const handleSaveVehicle = async () => {
    try {
      const vehicleData = prepareVehicleData();
      await createVehicle(vehicleData).unwrap();
      dispatch(setLastRadiusTravelled(vehicleData.radiusTravelled || ''));
      handleSuccess();
    } catch (err) {
      setVehicleSaveErrorToast(true);
      toggleToastState(setVehicleSaveErrorToast);
      console.error('Error saving vehicle:', err);
    }
  };
  const handleSuccess = () => {
    dispatch(setCurrentVehicle(emptyVehicle));
    dispatch(toggleVehicleToast(true));
    linkTo(URLS.VEHICLE.BYPASS);
  };
  const handleUpdateVehicle = async () => {
    let { id } = routeValue as { id?: number };
    try {
      const vehicleData = prepareVehicleData();

      await updateVehicle({
        id: id as number,
        updateVehicleData: vehicleData,
      }).unwrap();
      handleSuccess();
    } catch (err) {
      setVehicleSaveErrorToast(true);
      toggleToastState(setVehicleSaveErrorToast);
      console.error('Error updating vehicle:', err);
    }
  };

  const handleSubmit = () => {
    // gtm evnt when user press save
    trackGTMEvent(
      'vehicle-details-confirmed',
      {
        buttonTitle: 'Save',
      },
      accountType,
    );

    if (isUpdateMode) {
      handleUpdateVehicle();
    } else {
      handleSaveVehicle();
    }
  };

  const handleBack = () => {
    // gtm event when user press back
    trackGTMEvent(
      'vehicle-edit-back-button-pressed',
      {
        buttonTitle: 'Back',
      },
      accountType,
    );

    if (routeValue) {
      let { id } = routeValue as { id?: number };
      linkTo(`${URLS.VEHICLE.START}/${id}`);
    } else {
      linkTo(URLS.VEHICLE.START);
    }
  };

  const handleIsLoaned = (value: boolean) => {
    setIsLoan(value);

    if (!value) {
      handleChange('lossPayeeName', '');
      // Clear address when loan is toggled off
      setLossPayeeAddress(initialMapAddress);
      setVehicleDetails((prevDetails) => ({
        ...prevDetails,
        lossPayeeAddress: null,
      }));
    }
  };

  const handleStateValueChange = (text: string) => {
    let cleaned = text.trim(); // Remove leading and trailing spaces

    // Remove commas if any
    cleaned = cleaned.replace(/,/g, '');

    // Validate the number is numeric and within the correct length
    if (/^\d{0,7}$/.test(cleaned)) {
      setStateValue(cleaned);
    }
  };

  const handleEquipmentValueChange = (text: string) => {
    let cleaned = text.trim(); // Remove leading and trailing spaces

    // Remove commas if any
    cleaned = cleaned.replace(/,/g, '');

    // Validate the number is numeric and within the correct length
    if (/^\d{0,7}$/.test(cleaned)) {
      setEquipmentValue(cleaned);
    }
  };

  return (
    <ScrollView
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
      bounces={false}>
      <RadioModal
        testID={'vehicle-add'}
        isVisible={modalConfig.isVisible}
        onSelectOption={(value) => handleChange(modalConfig.type as keyof Vehicle, value)}
        onClose={() => setModalConfig({ ...modalConfig, isVisible: false })}
        title={modalConfig.title}
        options={modalConfig.options}
        onButtonClickLeft={() => setModalConfig({ ...modalConfig, isVisible: false })}
        selectedValue={vehicleDetails[modalConfig.type as keyof Vehicle] as string}
        splitLabels={modalConfig.type === 'classCode'}
      />
      <Typography variant="h2">Edit more details</Typography>
      <TourWrapper tourId="vehicle-type-switch">
        <SwitchButtons
          testID={'vehicle-type'}
          title={'Choose the Type of Vehicle'}
          active={isTruck}
          onChange={(value) => {
            setIsTruck(value);
            handleChange('classCode', '');
            handleChange('bodyType', '');
            handleChange('type', value ? 'truck' : 'trailer');
          }}
          buttonLeftText="Truck"
          buttonRightText="Trailer"
        />
      </TourWrapper>
      <TourWrapper tourId="vehicle-basic-info">
        <View>
          <InputField
            testID={'vehicle-add-year'}
            variant="caption"
            label={'Year'}
            placeholder="Please enter year of manufacturer"
            maxLength={4}
            value={vehicleDetails.year}
            onChange={(text) => handleChange('year', text)}
            isError={vehicleDetails.year?.length !== 4}
            errorText="Year must be 4 digits"
          />
          <InputField
            testID={'vehicle-add-make'}
            variant="caption"
            label={'Make'}
            placeholder="Please enter name of manufacturer"
            maxLength={250}
            value={vehicleDetails.make}
            onChange={(text) => handleChange('make', text)}
          />
          <InputField
            testID={'vehicle-add-model'}
            variant="caption"
            label={'Model'}
            placeholder="Please enter model of vehicle"
            maxLength={250}
            value={vehicleDetails.model}
            onChange={(text) => handleChange('model', text)}
          />
        </View>
      </TourWrapper>
      {isTruck && (
        <IconInput
          testID={'vehicle-add-truck-class'}
          label="Class"
          value={vehicleDetails.classCode as string}
          placeholder="Please choose the class of the truck"
          onPress={() => openModal('classCode')}
          iconSource={arrowDown}
          iconHeight={6.67}
          iconWidth={16.67}
        />
      )}

      <IconInput
        label="Body Type"
        testID={`vehicle-add-body-type-${isTruck ? 'truck' : 'trailer'}`}
        value={vehicleDetails.bodyType as string}
        placeholder={
          isTruck
            ? 'Please choose the body type of the truck'
            : 'Please choose the body type of the trailer'
        }
        onPress={() => openModal('bodyType')}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />

      <IconInput
        testID={'vehicle-add-travel-distance'}
        label="How far do you travel with this vehicle?"
        value={vehicleDetails.radiusTravelled as string}
        placeholder="Please choose the approximate miles radius"
        onPress={() => openModal('radiusTravelled')}
        iconSource={arrowDown}
        iconHeight={6.67}
        iconWidth={16.67}
      />
      {accountType === 'producer' && (
        <>
          <IconInput
            testID={'vehicle-valuation-type'}
            label="Valuation Type"
            value={valuationType}
            placeholder="Select valuation type"
            onPress={() => setIsValuationTypeModalVisible(true)}
            iconSource={arrowDown}
            iconHeight={6.67}
            iconWidth={16.67}
          />
          <RadioModal
            testID={'vehicle-valuation-type-modal'}
            isVisible={isValuationTypeModalVisible}
            onSelectOption={(value) => {
              setValuationType(value);
              setIsValuationTypeModalVisible(false);
            }}
            onButtonClickLeft={() => setIsValuationTypeModalVisible(false)}
            onClose={() => setIsValuationTypeModalVisible(false)}
            title="Select valuation type"
            options={[{ label: 'Actual Cash Value' }, { label: 'Stated Value' }]}
            selectedValue={valuationType}
          />
        </>
      )}
      <TourWrapper tourId="vehicle-stated-value">
        <InputField
          testID={'vehicle-add-stated-amount'}
          variant="amount"
          label={'Stated Value'}
          placeholder="0"
          value={stateValue} //this will need update when backend is updated
          onChange={(text) => handleStateValueChange(text)}
        />
      </TourWrapper>
      <InputField
        testID={'vehicle-add-attach-equpmnt'}
        variant="amount"
        label={'Value of permanently attached equipment'}
        placeholder="0"
        value={equipmentValue} //this will need update when backend is updated
        onChange={(text) => handleEquipmentValueChange(text)}
      />
      <SwitchButtons
        testID={'vehicle-add-loan-lease'}
        title={'Is there a loan or lease on this vehicle?'}
        active={isLoan}
        onChange={handleIsLoaned}
      />
      {isLoan && (
        <>
          <InputField
            testID={'vehicle-add-lease-owner-name'}
            variant="caption"
            label={'Name of Loss Payee / Lienholder / Finance Company'}
            placeholder="Please enter the name of the loss payee"
            value={vehicleDetails.lossPayeeName as string}
            onChange={(text) => handleChange('lossPayeeName', text)}
          />
          <IconInput
            testID={'vehicle-add-loss-payee-address'}
            label="Address of Loss Payee / Lienholder / Finance Company"
            value={
              lossPayeeAddress.street && lossPayeeAddress.street.length > 0
                ? formatAddressToString(lossPayeeAddress)
                : ''
            }
            placeholder="Please enter the address of the loss payee"
            iconSource={map}
            iconWidth={20}
            iconHeight={20}
            onPress={() => setIsMapVisible(true)}
          />
        </>
      )}
      {isLoan && isMapVisible && (
        <MapModal
          isVisible={isMapVisible}
          onClose={() => setIsMapVisible(false)}
          onSave={handleSaveLossPayeeAddress}
          address={lossPayeeAddress}
          initialPosition={{
            lat: lossPayeeAddress.latitude || 0,
            lng: lossPayeeAddress.longitude || 0,
          }}
          title="Add Loss Payee Address"
          testID={'vehicle-loss-payee-address'}
        />
      )}
      {vehicleSaveErrorToast && (
        <Toast
          type="error"
          text="An error occurred while adding this vehicle. Please try again."
          animate={true}
        />
      )}
      <View style={styles.buttonContainer}>
        <View style={styles.buttonWrapper1}>
          <Button
            type="tertiary"
            title="Back"
            onPress={handleBack}
            testID={'vehicle-add-back-btn'}
          />
        </View>
        <View style={styles.buttonWrapper2}>
          <TourWrapper tourId="vehicle-save-btn">
            <Button
              testID={'vehicle-add-next-btn'}
              type="primary"
              colorVariant={isSaveEnabled ? 'default' : 'disabled'}
              title="Save"
              onPress={handleSubmit}
            />
          </TourWrapper>
        </View>
      </View>
    </ScrollView>
  );
};

export default VehiclesEditScreen;
